import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Eye, EyeOff, Building2, Truck, Crown } from 'lucide-react';
import { subscribeToPush, requestNotificationPermission, isPushSupported } from '../utils/pushNotifications';
import { resetWelcomeSequence } from '../utils/welcomeTones';
import { PasskeyLoginButton, markPasskeyPromptPending, passkeyRegisteredHere } from '../components/Passkey';
import { FaceLoginCard, trustThisDevice } from '../components/FaceScan';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TAB_KEY = 'jmepl_login_tab';

const Login = ({ setAuth }) => {
  const [tab, setTab] = useState(localStorage.getItem(TAB_KEY) || 'customer');
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [custData, setCustData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [custError, setCustError] = useState('');
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState('staff'); // staff | customer
  const navigate = useNavigate();

  const switchTab = (t) => {
    setTab(t);
    localStorage.setItem(TAB_KEY, t);
  };

  const setupPushNotifications = async () => {
    if (isPushSupported()) {
      const permission = await requestNotificationPermission();
      if (permission === 'granted') {
        await subscribeToPush();
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: formData.email,
        password: formData.password
      });
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setAuth(true);
      sessionStorage.removeItem('jmepl_welcome_played');
      resetWelcomeSequence();
      setupPushNotifications();
      markPasskeyPromptPending('staff');
      trustThisDevice();
      toast.success(`Welcome, ${response.data.user.full_name || response.data.user.username}!`);
      navigate('/home');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const onPasskeySuccess = (data) => {
    trustThisDevice();
    if (data.store === 'staff') {
      localStorage.setItem('user', JSON.stringify(data.user));
      setAuth(true);
      sessionStorage.removeItem('jmepl_welcome_played');
      resetWelcomeSequence();
      setupPushNotifications();
      toast.success(`Welcome, ${data.user.full_name || data.user.username}!`);
      navigate('/home');
    } else {
      localStorage.setItem('portal_user', JSON.stringify(data.account));
      toast.success(`Welcome, ${data.account.customer_name}!`);
      navigate('/portal/dashboard');
    }
  };

  const handleCustomerLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setCustError('');
    try {
      const res = await axios.post(`${API}/portal/login`, custData, { withCredentials: true });
      localStorage.setItem('portal_user', JSON.stringify(res.data.account));
      markPasskeyPromptPending('portal');
      trustThisDevice();
      toast.success(`Welcome, ${res.data.account.customer_name}!`);
      navigate('/portal/dashboard');
    } catch (err) {
      const d = err.response?.data?.detail;
      const msg = typeof d === 'string' ? d : 'Login failed. Please try again.';
      setCustError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const openForgot = (mode) => {
    setForgotMode(mode);
    setForgotPasswordOpen(true);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      const path = forgotMode === 'customer' ? 'portal/forgot-password' : 'auth/forgot-password';
      await axios.post(`${API}/${path}`, { email: forgotEmail });
      toast.success('If an account with that email exists, a password reset link has been sent.');
      setForgotPasswordOpen(false);
      setForgotEmail('');
    } catch (error) {
      toast.error('Failed to send reset email. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };


  return (
    <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-4 safe-area-top safe-area-bottom relative overflow-hidden">
      {/* subtle brand glow */}
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-orange-500/10 blur-3xl rounded-full pointer-events-none" />

      <div className="text-center mb-7 relative z-10">
        <img src="/logo.png" alt="JMEPL Logo" className="w-20 h-20 mx-auto mb-3 rounded drop-shadow-2xl" />
        <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight leading-tight">JANARDAN MINERAL EXIM PVT LTD</h1>
        <div className="inline-block bg-gradient-to-r from-orange-500 to-orange-600 px-4 py-1.5 rounded-md shadow-lg mt-2">
          <p className="text-xs sm:text-sm font-black text-white tracking-wide">INDIA'S ONE OF THE BIGGEST SUPPLIER FOR PIG IRON</p>
        </div>
      </div>

      <div className="w-full max-w-md bg-white rounded-xl shadow-2xl overflow-hidden relative z-10">
        {/* Tabs */}
        <div className="grid grid-cols-2">
          <button onClick={() => switchTab('family')} data-testid="login-tab-family"
            className={`py-3.5 text-sm font-bold flex items-center justify-center gap-2 transition-colors ${tab === 'family' ? 'bg-white text-slate-900' : 'bg-slate-100 text-slate-400 hover:text-slate-600 border-b border-slate-200'}`}>
            <Building2 className="w-4 h-4" /> JMEPL Family Members
          </button>
          <button onClick={() => switchTab('customer')} data-testid="login-tab-customer"
            className={`py-3.5 text-sm font-bold flex items-center justify-center gap-2 transition-colors ${tab === 'customer' ? 'bg-white text-orange-600' : 'bg-slate-100 text-slate-400 hover:text-slate-600 border-b border-slate-200'}`}>
            <Truck className="w-4 h-4" /> Customer Portal
          </button>
        </div>

        <div className="p-6 sm:p-8">
          {tab === 'family' ? (
            <>
              <div className="mb-5">
                <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-slate-600" /> JMEPL Family Members Login
                </h2>
                <p className="text-xs text-slate-500 mt-1">For our team — logistics, marketing, accounts &amp; admin.</p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div data-testid="email-field">
                  <Label htmlFor="email" className="text-sm">Email</Label>
                  <Input id="email" type="email" data-testid="email-input" value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required className="mt-1 h-11 text-base" placeholder="you@jmepl.in" />
                </div>
                <div data-testid="password-field">
                  <Label htmlFor="password" className="text-sm">Password</Label>
                  <div className="relative mt-1">
                    <Input id="password" type={showPassword ? 'text' : 'password'} data-testid="password-input"
                      value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required className="h-11 text-base pr-11" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      data-testid="toggle-password-visibility" tabIndex={-1}>
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" data-testid="submit-button" disabled={loading}
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white h-12 text-base touch-target">
                  {loading ? 'Please wait...' : 'Sign In'}
                </Button>
                <div className="flex items-center gap-2 text-[11px] text-slate-400 uppercase tracking-wide"><span className="flex-1 h-px bg-slate-200" />or<span className="flex-1 h-px bg-slate-200" /></div>
                <PasskeyLoginButton onSuccess={onPasskeySuccess} testid="staff-passkey-login-btn" auto onNeedsSetup={() => document.querySelector('[data-testid="email-input"]')?.focus()} />
                <FaceLoginCard onSuccess={onPasskeySuccess} auto={!passkeyRegisteredHere()} />
              </form>
              <div className="mt-4 text-center">
                <button onClick={() => openForgot('staff')} data-testid="forgot-password-link"
                  className="text-sm text-orange-600 hover:text-orange-800 transition-colors touch-target py-1">
                  Forgot your password?
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="mb-5">
                <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                  <Truck className="w-5 h-5 text-orange-500" /> Customer Vehicle Placement Portal
                </h2>
                <p className="text-xs font-semibold text-orange-600 mt-1 flex items-center gap-1">
                  <Crown className="w-3.5 h-3.5" /> For Our Highly Valuable &amp; Premium Customers
                </p>
                <p className="text-xs text-slate-500 mt-1">Login with the credentials provided by JMEPL to place your vehicles.</p>
              </div>
              <form onSubmit={handleCustomerLogin} className="space-y-4">
                <div>
                  <Label htmlFor="cust-email" className="text-sm">Email</Label>
                  <Input id="cust-email" type="email" data-testid="customer-login-email" value={custData.email}
                    onChange={(e) => setCustData({ ...custData, email: e.target.value })}
                    required className="mt-1 h-11 text-base" placeholder="your@email.com" />
                </div>
                <div>
                  <Label htmlFor="cust-password" className="text-sm">Password</Label>
                  <div className="relative mt-1">
                    <Input id="cust-password" type={showPassword ? 'text' : 'password'} data-testid="customer-login-password"
                      value={custData.password} onChange={(e) => setCustData({ ...custData, password: e.target.value })}
                      required className="h-11 text-base pr-11" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" tabIndex={-1}>
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                {custError && (
                  <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="customer-login-error">{custError}</p>
                )}
                <Button type="submit" data-testid="customer-login-submit" disabled={loading}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white h-12 text-base font-semibold touch-target">
                  {loading ? 'Please wait...' : 'Login to Portal'}
                </Button>
                <div className="flex items-center gap-2 text-[11px] text-slate-400 uppercase tracking-wide"><span className="flex-1 h-px bg-slate-200" />or<span className="flex-1 h-px bg-slate-200" /></div>
                <PasskeyLoginButton onSuccess={onPasskeySuccess} testid="customer-passkey-login-btn" auto className="border-orange-500 text-orange-700 hover:bg-orange-500" onNeedsSetup={() => document.querySelector('[data-testid="customer-login-email"]')?.focus()} />
                <FaceLoginCard onSuccess={onPasskeySuccess} auto={!passkeyRegisteredHere()} accent="orange" />
              </form>
              <div className="mt-4 text-center">
                <button onClick={() => openForgot('customer')} data-testid="customer-forgot-password-link"
                  className="text-sm text-orange-600 hover:text-orange-800 transition-colors touch-target py-1">
                  Forgot your password?
                </button>
              </div>
              <p className="text-[11px] text-slate-400 mt-4 text-center">
                No account? Contact JMEPL marketing team to get portal access.
              </p>
            </>
          )}
        </div>
      </div>

      <p className="text-[11px] text-slate-600 mt-6 relative z-10">© JANARDAN MINERAL EXIM PVT LTD — JMEPL Support</p>

      {/* Forgot Password Dialog (staff + customer) */}
      <Dialog open={forgotPasswordOpen} onOpenChange={setForgotPasswordOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-md mx-2">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">Forgot Password{forgotMode === 'customer' ? ' — Customer Portal' : ''}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleForgotPassword} className="space-y-4 mt-4">
            <p className="text-sm text-slate-600">
              Enter your email address and we'll send you a link to reset your password.
            </p>
            <div>
              <Label htmlFor="forgot_email" className="text-sm">Email Address</Label>
              <Input id="forgot_email" type="email" data-testid="forgot-password-email" value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)} required className="mt-1 h-11"
                placeholder="Enter your email" />
            </div>
            <Button type="submit" disabled={forgotLoading} data-testid="forgot-password-submit"
              className="w-full bg-orange-500 hover:bg-orange-600 h-11 touch-target">
              {forgotLoading ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;
